import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@material-ui/core"
import { useSnackbar } from "../../../utils/notificacoes"
import { useEffect, useRef, useState } from "react"
import { atualizarInsumo, cadastrarInusmo } from "../../../services/insumos"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import {
    formatarQuantidadeParaEnviar,
    formatarValorParaEnviar,
    formatQuantidadeParaView,
    formatValorUnitarioParaView,
} from "../utils/formatacao"

const useStyles = makeStyles({
    root: { width: "100%" },
    container: { maxHeight: 440 },
    titulo: { paddingBottom: 0 },
})

export default function DialogInsumo({
    insumoModal,
    openDialog,
    setOpenDialog,
    setInsumos,
    insumos,
}) {
    const setLoading = useLoadingStore(s => s.setLoading)
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const alertStart = (msg, tipo) => {
        enqueueSnackbar(msg, { variant: tipo })
    }
    const configs = useAppStore(s => s.configuracoes)
    const idEstabelecimentoAtual = configs?.user?.parametros?.estabelecimento?.id
    const autorizacao = localStorage.getItem("chaveSistema")

    const nome = useRef(insumoModal?.nome)
    const valorUnitario = useRef(insumoModal?.valor_unitario ?? "0,00")
    const descricao = useRef(insumoModal?.descricao)

    const [unidadeDeMedida, setUnidadeDeMedida] = useState(insumoModal?.unidade_medida || "")
    const [controlarEstoque, setControlarEstoque] = useState(false)
    const [estoqueNegativo, setEstoqueNegativo] = useState(insumoModal?.estoque_negativo ?? false)
    const [quantidadeMinimaView, setQuantidadeMinimaView] = useState(() =>
        formatQuantidadeParaView(insumoModal?.estoque?.quantidade_minima)
    )
    const [quantidadeView, setQuantidadeView] = useState(() =>
        formatQuantidadeParaView(insumoModal?.estoque?.quantidade)
    )

    useEffect(() => {
        setControlarEstoque(insumoModal?.controlar_estoque || false)
        setUnidadeDeMedida(insumoModal?.unidade_medida || "")
        setEstoqueNegativo(insumoModal?.estoque_negativo ?? false)
        setQuantidadeMinimaView(formatQuantidadeParaView(insumoModal?.estoque?.quantidade_minima))
        setQuantidadeView(formatQuantidadeParaView(insumoModal?.estoque?.quantidade))
    }, [insumoModal])

    const handleSalvar = async () => {
        try {
            setLoading(true)
            const data = {
                nome: nome.current.value,
                unidadeDeMedida: unidadeDeMedida,
                valorUnitario: formatarValorParaEnviar(valorUnitarioView),
                descricao: descricao?.current?.value,
                quantidade_minima: formatarQuantidadeParaEnviar(quantidadeMinimaView),
                quantidade: formatarQuantidadeParaEnviar(quantidadeView),
                controlarEstoque: controlarEstoque,
                estoqueNegativo: estoqueNegativo,
            }

            if (insumoModal) {
                const response = await atualizarInsumo(
                    idEstabelecimentoAtual,
                    insumoModal.id,
                    data,
                    autorizacao
                )
                if (response.retornoErro === 1) {
                    alertStart(response.mensagem, "error")
                } else {
                    setInsumos(prev =>
                        prev.map(item => (item.id === response.id ? response : item))
                    )
                    alertStart("Insumo atualizado com sucesso", "success")
                }
            } else {
                const response = await cadastrarInusmo(idEstabelecimentoAtual, data, autorizacao)
                if (response.retornoErro === 1) {
                    alertStart(response.mensagem, "error")
                } else {
                    if (insumos.length >= 1) {
                        setInsumos(prev => [...prev, response])
                    } else {
                        setInsumos([response])
                    }
                    alertStart("Insumo cadastrado com sucesso", "success")
                }
                setUnidadeDeMedida("")
                setControlarEstoque(false)
            }
        } catch (error) {
            alertStart("Erro: " + error, "error")
        } finally {
            setOpenDialog(false)
            setLoading(false)
        }
    }

    const [valorUnitarioView, setValorUnitarioView] = useState(() =>
        formatValorUnitarioParaView(insumoModal?.valor_unitario ?? "")
    )

    useEffect(() => {
        setValorUnitarioView(formatValorUnitarioParaView(insumoModal?.valor_unitario ?? ""))
    }, [insumoModal?.valor_unitario])

    return (
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle className={classes.titulo}>
                {insumoModal ? "Editar Insumo" : "Cadastrar Insumo"}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={6}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Nome"
                            inputRef={nome}
                            InputLabelProps={{ shrink: true }}
                            placeholder="Cheddar"
                            defaultValue={insumoModal?.nome}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Unidade de medida</InputLabel>
                            <Select
                                label="Unidade de medida"
                                value={unidadeDeMedida}
                                onChange={e => setUnidadeDeMedida(e.target.value)}
                            >
                                <MenuItem value="">-</MenuItem>
                                <MenuItem value="U">Unidade</MenuItem>
                                <MenuItem value="G">Grama</MenuItem>
                                <MenuItem value="KG">Quilograma</MenuItem>
                                <MenuItem value="L">Litro</MenuItem>
                                <MenuItem value="ML">Mililitro</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Valor unitário"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">R$</InputAdornment>
                                ),
                                inputMode: "decimal",
                            }}
                            value={valorUnitarioView}
                            onChange={e => setValorUnitarioView(e.target.value)}
                            onBlur={() =>
                                setValorUnitarioView(formatValorUnitarioParaView(valorUnitarioView))
                            }
                            InputLabelProps={{ shrink: true }}
                            placeholder="0,00"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Descrição"
                            defaultValue={insumoModal?.descricao}
                            inputRef={descricao}
                            InputLabelProps={{ shrink: true }}
                            placeholder="..."
                        />
                    </Grid>
                    <Grid
                        item
                        container
                        xs={6}
                        sm={6}
                        md={6}
                        style={{ display: "flex", alignItems: "center" }}
                    >
                        <Checkbox
                            size="small"
                            defaultChecked={controlarEstoque}
                            onClick={() => setControlarEstoque(e => !e)}
                        />
                        <Typography>Controlar Estoque</Typography>
                    </Grid>
                    <Grid
                        item
                        container
                        xs={6}
                        sm={6}
                        md={6}
                        style={{ display: "flex", alignItems: "center" }}
                    >
                        <Checkbox
                            size="small"
                            checked={estoqueNegativo}
                            onClick={() => setEstoqueNegativo(e => !e)}
                        />
                        <Typography>Permitir Estoque Negativo</Typography>
                    </Grid>
                    {controlarEstoque && (
                        <>
                            <Grid item xs={12} sm={6} md={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    label="Quantidade mínima"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ inputMode: "decimal" }}
                                    value={quantidadeMinimaView}
                                    onChange={e =>
                                        setQuantidadeMinimaView(e.target.value.replace(/\./g, ","))
                                    }
                                    onBlur={() =>
                                        setQuantidadeMinimaView(
                                            formatQuantidadeParaView(quantidadeMinimaView)
                                        )
                                    }
                                    placeholder="10"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    label="Quantidade Atual"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ inputMode: "decimal" }}
                                    value={quantidadeView}
                                    onChange={e =>
                                        setQuantidadeView(e.target.value.replace(/\./g, ","))
                                    }
                                    onBlur={() =>
                                        setQuantidadeView(formatQuantidadeParaView(quantidadeView))
                                    }
                                    placeholder="30"
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogContent
                style={{
                    display: "flex",
                    justifyContent: "end",
                    gap: "0.5em",
                }}
            >
                <Button className={classes.cancelar} onClick={() => setOpenDialog(false)}>
                    Cancelar
                </Button>
                <Button className={classes.salvar} onClick={handleSalvar}>
                    Salvar
                </Button>
            </DialogContent>
        </Dialog>
    )
}
